import { QueryClient, useQuery } from '@tanstack/react-query';
import request from '@utils/request';
import { SpaEntityType } from 'shared-types/Spa';

export const getSpaFacility = async (
  propertyId: number,
  language: string,
  spaFacilityId: any,
  params: {
    arrivalDate: string | null;
    numberOfGuests?: number;
    showUnpublished?: number;
  }
) => {
  const { data } = await request.get(`${propertyId}/facilities/${spaFacilityId}/${language}`, {
    params,
  });
  return data as SpaEntityType;
};

export const getSpaFacilityPrefetch = async (
  queryClient: QueryClient,
  propertyId: number,
  language: string,
  spaFacilityId: any,
  arrivalDate: string | null,
  showUnpublished = false,
  numberOfGuests?: number
) => {
  await queryClient.prefetchQuery(
    getAvailableSpaFacilityKey(
      propertyId,
      language,
      spaFacilityId,
      {
        arrivalDate,
        numberOfGuests,
      },
      showUnpublished
    ),
    () =>
      getSpaFacility(propertyId, language, spaFacilityId, {
        arrivalDate,
        ...(numberOfGuests !== undefined ? { numberOfGuests } : {}),
        ...(showUnpublished ? { showUnpublished: 1 } : {}),
      }),
    { cacheTime: Infinity, staleTime: Infinity }
  );
};

export const useGetSpaFacility = (
  propertyId: number,
  language: string,
  spaFacilityId: any,
  arrivalDate: string | null,
  enabled = true,
  numberOfGuests?: number
) => {
  const { data, isFetching } = useQuery({
    queryKey: getAvailableSpaFacilityKey(
      propertyId,
      language,
      spaFacilityId,
      {
        arrivalDate,
        numberOfGuests,
      },
      false
    ),
    queryFn: () =>
      getSpaFacility(propertyId, language, spaFacilityId, {
        arrivalDate,
        ...(numberOfGuests !== undefined ? { numberOfGuests } : {}),
      }),
    cacheTime: Infinity,
    staleTime: Infinity,
    keepPreviousData: true,
    enabled,
  });
  return { data, isFetching };
};

export const getAvailableSpaFacilityKey = (
  propertyId: number,
  language: string,
  spaFacilityId: any,
  params: {
    arrivalDate: string | null;
    numberOfGuests?: number;
  },
  showUnpublished: boolean
) => [
  'spaFacility',
  'available',
  propertyId,
  parseInt(spaFacilityId, 10),
  ...Object.values(params),
  language,
  showUnpublished,
];
